/**
 * useCachedFetch - React Hook for cached API fetching
 * 
 * Provides automatic caching, loading states, and error handling
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import apiCache from './apiCache';

/**
 * Cache TTL presets (in milliseconds)
 */
export const CacheTTL = {
  SHORT: 1 * 60 * 1000,      // 1 minute - for frequently changing data
  MEDIUM: 5 * 60 * 1000,     // 5 minutes - default
  LONG: 15 * 60 * 1000,      // 15 minutes - for rarely changing data
  VERY_LONG: 60 * 60 * 1000, // 1 hour - for static data
};

/**
 * Invalidate cache for specific resource types
 */
export const invalidateCache = {
  customer: () => apiCache.invalidateContaining('/portal/'),
  nutrition: () => apiCache.invalidateContaining('/nutrition/'),
  appointments: () => apiCache.invalidateContaining('/appointments'),
  packages: () => apiCache.invalidateContaining('/packages'),
  invoices: () => apiCache.invalidateContaining('/invoices'),
  all: () => apiCache.clear(),
  key: (key) => apiCache.invalidate(key)
};

/**
 * Custom hook for cached data fetching
 * @param {string} cacheKey - Unique identifier for this data
 * @param {Function} fetchFn - Async function that returns the data
 * @param {Object} options - Configuration options
 * @param {number} options.ttl - Cache TTL in ms (default: 5 min)
 * @param {boolean} options.enabled - Whether to fetch (default: true)
 * @param {Array} options.deps - Additional dependencies for refetch
 */
export function useCachedFetch(cacheKey, fetchFn, options = {}) {
  const {
    ttl = CacheTTL.MEDIUM,
    enabled = true,
    deps = []
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchFnRef = useRef(fetchFn);
  
  // Update ref when fetchFn changes
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  const fetch = useCallback(async (forceRefresh = false) => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = apiCache.get(cacheKey);
      if (cached) {
        setData(cached);
        setLoading(false);
        return cached;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFnRef.current();
      apiCache.set(cacheKey, result, ttl);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      console.error(`Fetch error for ${cacheKey}:`, err);
      // Don't re-throw - error is stored in state for component to handle
      return null;
    } finally {
      setLoading(false);
    }
  }, [cacheKey, ttl, enabled]);

  // Initial fetch
  useEffect(() => {
    fetch().catch(() => {
      // Error already handled in fetch function
    });
  }, [fetch, ...deps]);

  // Force refresh function
  const refresh = useCallback(() => {
    return fetch(true);
  }, [fetch]);

  // Invalidate cache without refetching
  const invalidate = useCallback(() => {
    apiCache.invalidate(cacheKey);
  }, [cacheKey]);

  return {
    data,
    loading,
    error,
    refresh,
    invalidate
  };
}

/**
 * Prefetch data into cache
 * Useful for preloading data that will be needed soon
 */
export function usePrefetch() {
  const prefetch = useCallback(async (cacheKey, fetchFn, ttl = CacheTTL.MEDIUM) => {
    // Don't prefetch if already cached
    if (apiCache.get(cacheKey)) {
      return;
    }

    try {
      const result = await fetchFn();
      apiCache.set(cacheKey, result, ttl);
    } catch (err) {
      console.error(`Prefetch error for ${cacheKey}:`, err);
    }
  }, []);

  return prefetch;
}

export default useCachedFetch;
